import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { connect } from "react-redux";
import { Button } from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import { Content } from "antd/es/layout/layout";
import Loader from "../../../Home/Loader/Loader";
import { getPartialUser } from "../../../redux/actions/API/users";
import "../previewDocument/PreviewDocument.css";
import { getGateEntriesById } from "../../../redux/actions/API/gateEntry";

export interface GateEntry {
  id: number;
  userId: number;
  documentNumber: string;
  visitorName: string;
  visitorContact: string;
  visitorEmail?: string | null;
  visitorCompany?: string | null;
  idProofType?: string | null;
  idProofNumber?: string | null;
  purposeOfVisit: string;
  visitorImageUrl: string;
  personToMeet: string;
  vehicleNumber?: string | null;
  vehicleType?: string | null;
  comments?: string | null;
  companyId: number;
  status: number;
  securitySignatureUrl: string;
  createdAt: string;
  updatedAt?: string | null;
  user: {
    id: number;
    name: string;
    email: string;
    contactNo: string;
  };
}

interface IDownloadGateEntryPdfProps {
  gateEntryData: GateEntry;
  getGateEntryByID: Function;
  getPartialUser: Function;
  loading: boolean;
  userData?: any;
}

const statusMap: Record<number, { label: string; color: string }> = {
  0: { label: "Pending", color: "#faad14" },
  1: { label: "Exited", color: "#52c41a" },
  2: { label: "Checked Out", color: "#1677ff" },
  3: { label: "Rejected", color: "#ff4d4f" },
};

const DownloadGateEntryPdf: React.FC<IDownloadGateEntryPdfProps> = ({
  gateEntryData,
  getGateEntryByID,
  getPartialUser,
  loading,
  userData,
}) => {
  const [searchParams] = useSearchParams();
  const gateEntryId = searchParams.get("gateEntryId");
  const companyId = searchParams.get("clientId");
  const [decodedSupplier, setDecodedSupplier] = useState<any>({});

  useEffect(() => {
    getGateEntryByID({
      id: Number(gateEntryId),
    //   companyId: Number(companyId),
    });
    getPartialUser({ companyId: Number(companyId) });
  }, [gateEntryId, companyId]);

  useEffect(() => {
    if (userData) setDecodedSupplier(userData);
  }, [userData]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return (
      date.toLocaleDateString("en-GB") +
      " " +
      date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    );
  };

  const getStatusInfo = (status: number) => {
    return statusMap[status] || { label: "Unknown", color: "#999" };
  };

  return (
    <div
      className="whatsapp-print"
      style={{
        display: "flex",
        width: "100%",
        gap: "10px",
        flexDirection: "column",
      }}
    >
      <Loader loading={loading} />
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button type="link" onClick={() => window.print()}>
          <PrinterOutlined /> Print
        </Button>
      </div>

      {gateEntryData?.documentNumber && (
        <Content>
          <div
            className="documentContainer"
            id="documentContainer"
            style={{
              width: "1000px",
              fontSize: "14px",
              margin: "auto",
              marginTop: "5px",
              padding: "10px",
            }}
          >
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "20px",
              }}
            >
              <div>
                {decodedSupplier?.logoUrl && (
                  <img
                    src={decodedSupplier.logoUrl}
                    alt="EaseMargin"
                    style={{ maxHeight: "70px", marginBottom: "8px" }}
                  />
                )}
              </div>
              <div style={{ textAlign: "right" }}>
                <h3 style={{ margin: 0 }}>Gate Entry Pass</h3>
                <div className="title">
                  Entry No: {gateEntryData.documentNumber}
                </div>
                <div
                  style={{
                    marginTop: "6px",
                    padding: "2px 12px",
                    borderRadius: "4px",
                    display: "inline-block",
                    fontWeight: 500,
                    color: getStatusInfo(gateEntryData.status).color,
                    border: `1px solid ${getStatusInfo(gateEntryData.status).color}`,
                  }}
                >
                  {getStatusInfo(gateEntryData.status).label}
                </div>
              </div>
            </div>

            <hr />

            {/* Visitor Details & Photo */}
            <div
              style={{
                display: "flex",
                width: "100%",
                gap: "20px",
                marginTop: "15px",
              }}
            >
              {/* Visitor Info */}
              <div
                style={{
                  flex: 2,
                  borderRight: "1px solid #ccc",
                  paddingRight: "20px",
                }}
              >
                <h6 style={{ marginBottom: "10px" }}>Visitor Details</h6>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                  }}
                >
                  <tbody>
                    <tr>
                      <td
                        style={{
                          padding: "6px 8px",
                          fontWeight: 500,
                          width: "40%",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        Visitor Name
                      </td>
                      <td
                        style={{
                          padding: "6px 8px",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        {gateEntryData.visitorName}
                      </td>
                    </tr>
                    <tr>
                      <td
                        style={{
                          padding: "6px 8px",
                          fontWeight: 500,
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        Contact Number
                      </td>
                      <td
                        style={{
                          padding: "6px 8px",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        +91-{gateEntryData.visitorContact}
                      </td>
                    </tr>
                    {gateEntryData.visitorEmail && (
                      <tr>
                        <td
                          style={{
                            padding: "6px 8px",
                            fontWeight: 500,
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          Email
                        </td>
                        <td
                          style={{
                            padding: "6px 8px",
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          {gateEntryData.visitorEmail}
                        </td>
                      </tr>
                    )}
                    {gateEntryData.visitorCompany && (
                      <tr>
                        <td
                          style={{
                            padding: "6px 8px",
                            fontWeight: 500,
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          Visitor Company
                        </td>
                        <td
                          style={{
                            padding: "6px 8px",
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          {gateEntryData.visitorCompany}
                        </td>
                      </tr>
                    )}
                    {gateEntryData.idProofType && (
                      <tr>
                        <td
                          style={{
                            padding: "6px 8px",
                            fontWeight: 500,
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          ID Proof Type
                        </td>
                        <td
                          style={{
                            padding: "6px 8px",
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          {gateEntryData.idProofType}
                        </td>
                      </tr>
                    )}
                    {gateEntryData.idProofNumber && (
                      <tr>
                        <td
                          style={{
                            padding: "6px 8px",
                            fontWeight: 500,
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          ID Proof Number
                        </td>
                        <td
                          style={{
                            padding: "6px 8px",
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          {gateEntryData.idProofNumber}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Visitor Photo */}
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "flex-start",
                }}
              >
                {gateEntryData.visitorImageUrl ? (
                  <img
                    src={gateEntryData.visitorImageUrl}
                    alt="Visitor"
                    style={{
                      width: "150px",
                      height: "180px",
                      objectFit: "cover",
                      borderRadius: "8px",
                      border: "1px solid #ccc",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: "150px",
                      height: "180px",
                      borderRadius: "8px",
                      border: "1px solid #ccc",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#999",
                      fontSize: "12px",
                    }}
                  >
                    No Photo
                  </div>
                )}
                <div
                  style={{
                    marginTop: "6px",
                    fontSize: "12px",
                    color: "#666",
                  }}
                >
                  Visitor Photo
                </div>
              </div>
            </div>

            <hr style={{ margin: "15px 0" }} />

            {/* Visit Details */}
            <div style={{ marginTop: "10px" }}>
              <h6 style={{ marginBottom: "10px" }}>Visit Details</h6>
              <table
                className="documentTable"
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  border: "1px solid silver",
                }}
              >
                <thead>
                  <tr>
                    <th
                      style={{
                        border: "1px solid silver",
                        padding: "8px",
                        width: "25%",
                      }}
                    >
                      Purpose of Visit
                    </th>
                    <th
                      style={{
                        border: "1px solid silver",
                        padding: "8px",
                        width: "25%",
                      }}
                    >
                      Person to Meet
                    </th>
                    <th
                      style={{
                        border: "1px solid silver",
                        padding: "8px",
                        width: "25%",
                      }}
                    >
                      Entry Time
                    </th>
                    {gateEntryData.updatedAt && (gateEntryData.status === 1 || gateEntryData.status === 2) && (
                      <th
                        style={{
                          border: "1px solid silver",
                          padding: "8px",
                          width: "25%",
                        }}
                      >
                        Exit Time
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td
                      style={{
                        border: "1px solid silver",
                        padding: "8px",
                      }}
                    >
                      {gateEntryData.purposeOfVisit}
                    </td>
                    <td
                      style={{
                        border: "1px solid silver",
                        padding: "8px",
                      }}
                    >
                      {gateEntryData.personToMeet}
                    </td>
                    <td
                      style={{
                        border: "1px solid silver",
                        padding: "8px",
                      }}
                    >
                      {formatDate(gateEntryData.createdAt)}
                    </td>
                    {gateEntryData.updatedAt && (gateEntryData.status === 1 || gateEntryData.status === 2) && (
                      <td
                        style={{
                          border: "1px solid silver",
                          padding: "8px",
                        }}
                      >
                        {formatDate(gateEntryData.updatedAt)}
                      </td>
                    )}
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Vehicle Details */}
            {(gateEntryData.vehicleNumber || gateEntryData.vehicleType) && (
              <div style={{ marginTop: "20px" }}>
                <h6 style={{ marginBottom: "10px" }}>Vehicle Details</h6>
                <table
                  className="documentTable"
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    border: "1px solid silver",
                  }}
                >
                  <thead>
                    <tr>
                      {gateEntryData.vehicleType && (
                        <th
                          style={{
                            border: "1px solid silver",
                            padding: "8px",
                          }}
                        >
                          Vehicle Type
                        </th>
                      )}
                      {gateEntryData.vehicleNumber && (
                        <th
                          style={{
                            border: "1px solid silver",
                            padding: "8px",
                          }}
                        >
                          Vehicle Number
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {gateEntryData.vehicleType && (
                        <td
                          style={{
                            border: "1px solid silver",
                            padding: "8px",
                          }}
                        >
                          {gateEntryData.vehicleType}
                        </td>
                      )}
                      {gateEntryData.vehicleNumber && (
                        <td
                          style={{
                            border: "1px solid silver",
                            padding: "8px",
                          }}
                        >
                          {gateEntryData.vehicleNumber}
                        </td>
                      )}
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Authorized By */}
            {gateEntryData.user && (
              <>
                <hr style={{ margin: "15px 0" }} />
                <div style={{ marginTop: "10px" }}>
                  <h6 style={{ marginBottom: "10px" }}>Authorized By</h6>
                  <div>
                    <span className="title">Name: </span>
                    {gateEntryData.user.name}
                  </div>
                  {gateEntryData.user.email && (
                    <div>
                      <span className="title">Email: </span>
                      {gateEntryData.user.email}
                    </div>
                  )}
                  {gateEntryData.user.contactNo && (
                    <div>
                      <span className="title">Contact: </span>
                      +91-{gateEntryData.user.contactNo}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Comments */}
            {gateEntryData.comments && (
              <>
                <hr style={{ margin: "15px 0" }} />
                <div style={{ marginTop: "10px" }}>
                  <h6 style={{ marginBottom: "10px" }}>Comments</h6>
                  <div>{gateEntryData.comments}</div>
                </div>
              </>
            )}

            {/* Signatures */}
            <hr style={{ margin: "15px 0" }} />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
                marginTop: "20px",
              }}
            >
              {/* Visitor Signature */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    width: "150px",
                    height: "60px",
                    borderBottom: "1px solid #000",
                  }}
                />
                <p>Visitor Signature</p>
              </div>

              {/* Security Signature */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                {gateEntryData.securitySignatureUrl ? (
                  <img
                    src={gateEntryData.securitySignatureUrl}
                    alt="Security Signature"
                    style={{
                      objectFit: "contain",
                      maxWidth: "120px",
                      maxHeight: "80px",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: "150px",
                      height: "60px",
                      borderBottom: "1px solid #000",
                    }}
                  />
                )}
                <p>Security Signature</p>
              </div>
            </div>
          </div>
        </Content>
      )}
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  gateEntryData: state.api.getGateEntryByID?.data?.data,
  loading: state.api.getGateEntryByID?.loading,
  userData: state.api.getPartialUser?.data,
});

const mapDispatchToProps = (dispatch: any) => ({
  getGateEntryByID: (payload: any) =>
    // dispatch({ type: "GET_GATE_ENTRIES_BY_ID", payload }),
  dispatch(getGateEntriesById(payload)),
  getPartialUser: (payload: any) => dispatch(getPartialUser(payload)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(DownloadGateEntryPdf);
